import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Heart, Users, Target, BookOpen, Compass } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { specializations } from '../mockData';
import { presentation } from '../data/aboutContent';

const About = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const values = [
    {
      icon: Heart,
      title: "Bienveillance",
      description: "Un espace sécurisant et sans jugement où vous pouvez vous exprimer librement."
    },
    {
      icon: Users,
      title: "Écoute Active",
      description: "Une attention authentique portée à votre histoire et vos besoins uniques."
    },
    {
      icon: Target,
      title: "Approche Personnalisée",
      description: "Des méthodes adaptées à votre situation pour des résultats concrets."
    },
    {
      icon: Award,
      title: "Professionnalisme",
      description: "Une formation solide et une pratique éthique au service de votre bien-être."
    }
  ];

  return (
    <div className="min-h-screen bg-[#F5F3EB]">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-[#F5F3EB] -z-10"></div>

        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h1 className="font-display text-5xl md:text-6xl font-semibold text-[#21167D] mb-6">
              Qui suis-je ?
            </h1>
            <p className="font-accent italic text-xl text-[#4A4458] max-w-3xl mx-auto leading-relaxed">
              Psychologue et psychothérapeute passionnée par l'accompagnement humain
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className={`relative transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <div className="absolute -top-5 -left-5 w-full h-full bg-[#DCB071] rounded-t-[4rem] rounded-b-2xl -z-10"></div>
              <div className="relative rounded-t-[4rem] rounded-b-2xl overflow-hidden shadow-2xl border-4 border-[#F5F3EB]">
                <img
                  src="/tamila-portrait.jpg"
                  alt="Portrait de Tamila Hachemi, psychologue à Marseille"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>

            {/* Text Content */}
            <div className={`space-y-6 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <p className="font-accent italic text-xl text-[#21167D] font-semibold">{presentation.title}</p>
              <div className="space-y-4 text-lg text-[#4A4458] leading-relaxed">
                {presentation.paragraphs.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row flex-wrap gap-4 pt-6">
                <Link to="/rendez-vous">
                  <Button size="lg" className="shadow-lg">
                    Prendre rendez-vous
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/qui-suis-je/la-psychotherapie">
                  <Button size="lg" variant="outline">
                    <BookOpen className="mr-2 w-5 h-5" />
                    Mais c'est quoi la psychothérapie ?
                  </Button>
                </Link>
                <Link to="/mon-approche">
                  <Button size="lg" variant="outline">
                    <Compass className="mr-2 w-5 h-5" />
                    Mon approche
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 bg-[#FDFBF5]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-10 items-center mb-12">
            <div className="lg:col-span-2 text-center lg:text-left">
              <h2 className="font-display text-4xl font-semibold text-[#21167D] mb-4">
                Mes valeurs
              </h2>
              <p className="font-accent italic text-xl text-[#4A4458] max-w-2xl">
                Les principes qui guident ma pratique thérapeutique
              </p>
            </div>
            <img
              src="/illustration-esprit-fleuri.png"
              alt="Illustration : accompagner l'épanouissement personnel"
              className="hidden lg:block w-full max-w-xs mx-auto"
            />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-[#F5F3EB]">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-[#21167D] rounded-full flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-transform">
                    <value.icon className="w-8 h-8 text-[#F5F3EB]" />
                  </div>
                  <h3 className="font-accent font-semibold text-xl text-[#21167D] mb-3">{value.title}</h3>
                  <p className="text-[#4A4458] leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Secondary Portrait */}
      <section className="py-16 px-4 bg-[#F5F3EB]">
        <div className="max-w-md mx-auto">
          <div className="relative">
            <div className="absolute -bottom-4 -right-4 w-full h-full bg-[#DCB071] rounded-t-[3rem] rounded-b-2xl -z-10"></div>
            <div className="relative rounded-t-[3rem] rounded-b-2xl overflow-hidden shadow-xl border-4 border-[#FDFBF5]">
              <img
                src="/Tam_1.png"
                alt="Tamila Hachemi, psychologue"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Specializations Section */}
      <section className="py-20 px-4 bg-[#DCB071]/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl font-semibold text-[#21167D] mb-4">
              Mes domaines de spécialisation
            </h2>
            <p className="font-accent italic text-xl text-[#4A4458] max-w-3xl mx-auto">
              Une expertise développée pour répondre à vos besoins spécifiques
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {specializations.map((spec, index) => (
              <div
                key={index}
                className="group p-6 bg-[#FDFBF5] rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-[#DCB071]/30"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-[#EAA93D] rounded-full group-hover:scale-150 transition-transform"></div>
                  <p className="text-lg font-medium text-[#21167D]">{spec}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Banner Image Section */}
      <section className="py-0 px-0">
        <div className="relative w-full h-64 md:h-96 overflow-hidden">
          <img
            src="/photo-consultation.png"
            alt="Séance d'accompagnement psychologique"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#21167D]/25"></div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-[#21167D]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-4xl font-semibold text-[#F5F3EB] mb-6">
            Commençons ensemble ce voyage
          </h2>
          <p className="font-accent italic text-xl text-[#F5F3EB]/85 mb-8 leading-relaxed">
            Je suis là pour vous accompagner avec bienveillance dans votre cheminement personnel.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/rendez-vous">
              <Button size="lg" className="bg-[#EAA93D] text-[#21167D] hover:bg-[#F5F3EB] hover:text-[#21167D] shadow-xl">
                Prendre rendez-vous
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-[#F5F3EB] text-[#F5F3EB] hover:bg-[#F5F3EB] hover:text-[#21167D]">
                Découvrir mes services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
